/**
 * Serialisation du workspace HTML/CSS.
 *
 * Trois formats coexistent en base :
 *   - v2  : `{"v":2,"html":"...","css":"..."}` (format ecrit aujourd'hui)
 *   - v1  : `{"html":"...","css":"..."}`      (JSON sans numero de version)
 *   - legacy : `<html>\n\n/* CSS *\/\n<css>`  (concatenation historique)
 *
 * La lecture accepte les trois, l'ecriture ne produit que du v2. Le format
 * concatene etait ambigu : son separateur est un commentaire CSS valide, donc
 * un eleve pouvait le saisir et provoquer une decoupe au mauvais endroit.
 */

import { HtmlCssWorkspace } from './types';

/** Separateur du format historique, conserve pour la relecture uniquement. */
const LEGACY_SEPARATOR = '\n\n/* CSS */\n';

const WORKSPACE_VERSION = 2;

export const EMPTY_WORKSPACE: HtmlCssWorkspace = { html: '', css: '' };

/** Serialise un workspace dans le format courant. */
export const serializeWorkspace = (workspace: HtmlCssWorkspace): string =>
  JSON.stringify({ v: WORKSPACE_VERSION, html: workspace.html, css: workspace.css });

/**
 * Relit un workspace quel que soit son format d'origine.
 *
 * Retourne `null` lorsque la source est inexploitable, laissant a l'appelant le
 * soin de choisir son repli (workspace de reference de l'enseignant, valeurs
 * par defaut...). On ne retourne jamais un workspace partiel silencieusement :
 * c'est ce qui faisait perdre le travail des eleves auparavant.
 */
export const deserializeWorkspace = (source: unknown): HtmlCssWorkspace | null => {
  if (typeof source !== 'string' || source.trim() === '') return null;

  // Formats v2 et v1 : une seule tentative de parsing couvre les deux.
  try {
    const parsed = JSON.parse(source) as { html?: unknown; css?: unknown };
    if (parsed && typeof parsed === 'object') {
      const html = typeof parsed.html === 'string' ? parsed.html : null;
      const css = typeof parsed.css === 'string' ? parsed.css : null;
      if (html !== null || css !== null) {
        return { html: html ?? '', css: css ?? '' };
      }
    }
  } catch {
    // Pas du JSON : on tente le format concatene ci-dessous.
  }

  // Format historique. On coupe sur la premiere occurrence seulement, pour que
  // la presence du separateur dans le code de l'eleve ne multiplie pas les
  // fragments.
  const separatorIndex = source.indexOf(LEGACY_SEPARATOR);
  if (separatorIndex !== -1) {
    return {
      html: source.slice(0, separatorIndex),
      css: source.slice(separatorIndex + LEGACY_SEPARATOR.length),
    };
  }

  // Chaine non structuree : on la considere comme du HTML seul plutot que de
  // la rejeter, ce qui preserve un contenu qui serait autrement perdu.
  return { html: source, css: '' };
};

/** Indique si deux workspaces portent le meme contenu. */
export const workspacesAreEqual = (a: HtmlCssWorkspace, b: HtmlCssWorkspace): boolean =>
  a.html === b.html && a.css === b.css;
